import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, MailCheck, Megaphone, Store, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#2E1065]/15 focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 rounded-xl px-4 py-3 text-sm font-medium text-[#2E1065] placeholder:text-[#2E1065]/35 outline-none transition-all duration-300";

const BUDGETS = ["₹25K–50K", "₹50K–1L", "₹1L+"];
const VOLUMES = ["Under 200 cups/day", "200–500 cups/day", "500+ cups/day"];

const Chip = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-xs font-bold rounded-full px-3.5 py-2 transition-all duration-300 border ${
      selected
        ? "bg-[#0D9488] text-white border-[#0D9488]"
        : "bg-white text-[#2E1065]/70 border-[#2E1065]/15 hover:border-[#0D9488]/50"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

const PrivacyNote = () => (
  <p className="text-xs text-[#2E1065]/50 leading-relaxed" data-testid="enquiry-privacy-note">
    We use these details only to respond to your enquiry and coordinate a pilot.
    Never sold, never shared. See our{" "}
    <a href="/privacy" className="font-bold text-[#0D9488] underline underline-offset-2">Privacy Policy</a>.
  </p>
);

export const Contact = ({ journey, setJourney }) => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const [adv, setAdv] = useState({ name: "", email: "", business_name: "", city: "", volume: BUDGETS[0], message: "" });
  const [ven, setVen] = useState({ name: "", email: "", business_name: "", city: "", volume: VOLUMES[0], message: "" });

  const submit = async (e, role) => {
    e.preventDefault();
    const f = role === "advertiser" ? adv : ven;
    if (!f.name || !f.email || !f.message) {
      toast.error("Please add your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        role,
        name: f.name,
        email: f.email,
        business_name: f.business_name,
        city: f.city,
        volume: f.volume,
        subject: role === "advertiser" ? "Advertiser enquiry" : "Vendor partner application",
        message: f.message,
      });
      setSent(role);
      toast.success(role === "advertiser" ? "Advertiser enquiry sent!" : "Vendor application sent!", {
        description: "We reply within one business day.",
      });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSent(null);
    setAdv({ name: "", email: "", business_name: "", city: "", volume: BUDGETS[0], message: "" });
    setVen({ name: "", email: "", business_name: "", city: "", volume: VOLUMES[0], message: "" });
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-[#F3EFEA]" data-testid="contact-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-2"
        >
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#0D9488] mb-4" data-testid="contact-eyebrow">
            Two Ways In
          </p>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-[#2E1065] leading-tight" data-testid="contact-headline">
            Advertise on cups — or earn from serving them.
          </h2>
          <div className="mt-6 space-y-5">
            <div className="flex gap-4">
              <span className="w-11 h-11 rounded-2xl bg-[#FF5A36]/10 flex items-center justify-center shrink-0">
                <Megaphone className="w-5 h-5 text-[#FF5A36]" />
              </span>
              <p className="text-sm text-[#2E1065]/70 leading-relaxed">
                <span className="font-bold text-[#2E1065]">Brands & advertisers:</span> book a pilot
                campaign and put your brand in thousands of local hands.
              </p>
            </div>
            <div className="flex gap-4">
              <span className="w-11 h-11 rounded-2xl bg-[#0D9488]/10 flex items-center justify-center shrink-0">
                <Store className="w-5 h-5 text-[#0D9488]" />
              </span>
              <p className="text-sm text-[#2E1065]/70 leading-relaxed">
                <span className="font-bold text-[#2E1065]">Chai tapris, tea stalls & cafés:</span> join
                the vendor network — serve sponsored cups and cut your packaging cost.
              </p>
            </div>
            <div className="flex gap-4">
              <span className="w-11 h-11 rounded-2xl bg-[#FBBF24]/30 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-[#2E1065]" />
              </span>
              <p className="text-sm text-[#2E1065]/70 leading-relaxed self-center">
                One inbox for everything:{" "}
                <span className="font-bold text-[#2E1065]" data-testid="contact-email">enquiry@cupconnect.net</span>
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-3"
        >
          {sent ? (
            <div className="bg-white rounded-[2rem] p-10 text-center border border-[#0D9488]/20" data-testid="enquiry-success-state">
              <MailCheck className="w-12 h-12 text-[#0D9488] mx-auto mb-4" />
              <h3 className="font-display font-extrabold text-2xl text-[#2E1065]">
                {sent === "advertiser" ? "Advertiser enquiry received!" : "Vendor application received!"}
              </h3>
              <p className="text-[#2E1065]/60 mt-2">We will get back to you within one business day.</p>
              <button
                onClick={reset}
                className="mt-6 text-sm font-bold text-[#FF5A36] hover:underline"
                data-testid="enquiry-send-another-button"
              >
                Send another message
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-[2rem] p-7 sm:p-8 border border-[#2E1065]/10">
              <div className="flex bg-[#F3EFEA] rounded-full p-1.5 mb-7" data-testid="enquiry-journey-toggle">
                {[
                  { key: "advertiser", label: "I'm an Advertiser", icon: Megaphone },
                  { key: "vendor", label: "I'm a Vendor Partner", icon: Store },
                ].map((j) => (
                  <button
                    key={j.key}
                    onClick={() => setJourney(j.key)}
                    className={`flex-1 flex items-center justify-center gap-2 text-sm font-bold rounded-full py-3 transition-all duration-300 ${
                      journey === j.key ? "bg-[#2E1065] text-[#FBBF24] shadow-lg shadow-[#2E1065]/20" : "text-[#2E1065]/60"
                    }`}
                    data-testid={`enquiry-role-${j.key}`}
                  >
                    <j.icon className="w-4 h-4" /> {j.label}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                {journey === "advertiser" ? (
                  <motion.form
                    key="adv"
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 16 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={(e) => submit(e, "advertiser")}
                    className="space-y-4"
                    data-testid="advertiser-enquiry-form"
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input className={inputCls} placeholder="Your name *" value={adv.name} onChange={(e) => setAdv({ ...adv, name: e.target.value })} data-testid="adv-name-input" />
                      <input type="email" className={inputCls} placeholder="Work email *" value={adv.email} onChange={(e) => setAdv({ ...adv, email: e.target.value })} data-testid="adv-email-input" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input className={inputCls} placeholder="Brand / company name" value={adv.business_name} onChange={(e) => setAdv({ ...adv, business_name: e.target.value })} data-testid="adv-brand-input" />
                      <input className={inputCls} placeholder="Target city or area" value={adv.city} onChange={(e) => setAdv({ ...adv, city: e.target.value })} data-testid="adv-city-input" />
                    </div>
                    <div>
                      <p className="text-xs font-black uppercase tracking-widest text-[#2E1065]/50 mb-2.5">Pilot budget</p>
                      <div className="flex flex-wrap gap-2">
                        {BUDGETS.map((b) => (
                          <Chip key={b} testid={`adv-budget-${b.replace(/[₹K–+L]/g, "").trim() || "other"}-${BUDGETS.indexOf(b)}`} selected={adv.volume === b} onClick={() => setAdv({ ...adv, volume: b })}>
                            {b}
                          </Chip>
                        ))}
                      </div>
                    </div>
                    <textarea className={`${inputCls} min-h-[100px] resize-none`} placeholder="What would you like to advertise? *" value={adv.message} onChange={(e) => setAdv({ ...adv, message: e.target.value })} data-testid="adv-message-input" />
                    <PrivacyNote />
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 font-bold text-white bg-[#FF5A36] hover:bg-[#2E1065] rounded-full px-6 py-4 transition-all duration-300 disabled:opacity-60"
                      data-testid="adv-submit-button"
                    >
                      {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                      {loading ? "Sending…" : "Start My Pilot Enquiry"}
                    </button>
                  </motion.form>
                ) : (
                  <motion.form
                    key="ven"
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={(e) => submit(e, "vendor")}
                    className="space-y-4"
                    data-testid="vendor-enquiry-form"
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input className={inputCls} placeholder="Your name *" value={ven.name} onChange={(e) => setVen({ ...ven, name: e.target.value })} data-testid="ven-name-input" />
                      <input type="email" className={inputCls} placeholder="Email *" value={ven.email} onChange={(e) => setVen({ ...ven, email: e.target.value })} data-testid="ven-email-input" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input className={inputCls} placeholder="Stall / café name" value={ven.business_name} onChange={(e) => setVen({ ...ven, business_name: e.target.value })} data-testid="ven-business-input" />
                      <input className={inputCls} placeholder="City & area" value={ven.city} onChange={(e) => setVen({ ...ven, city: e.target.value })} data-testid="ven-city-input" />
                    </div>
                    <div>
                      <p className="text-xs font-black uppercase tracking-widest text-[#2E1065]/50 mb-2.5">Daily cups served</p>
                      <div className="flex flex-wrap gap-2">
                        {VOLUMES.map((v, i) => (
                          <Chip key={v} testid={`ven-volume-${i}`} selected={ven.volume === v} onClick={() => setVen({ ...ven, volume: v })}>
                            {v}
                          </Chip>
                        ))}
                      </div>
                    </div>
                    <textarea className={`${inputCls} min-h-[100px] resize-none`} placeholder="Tell us about your stall or café *" value={ven.message} onChange={(e) => setVen({ ...ven, message: e.target.value })} data-testid="ven-message-input" />
                    <PrivacyNote />
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 font-bold text-white bg-[#0D9488] hover:bg-[#2E1065] rounded-full px-6 py-4 transition-all duration-300 disabled:opacity-60"
                      data-testid="ven-submit-button"
                    >
                      {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                      {loading ? "Sending…" : "Apply as Vendor Partner"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
